<script setup>
import { computed, ref } from 'vue';
import {
  ArrowUpRight,
  Boxes,
  Check,
  CircleCheck,
  Eye,
  Factory,
  Layers3,
  LockKeyhole,
  Menu,
  Network,
  Play,
  ShieldCheck,
  Sparkles,
  Workflow,
  X
} from 'lucide-vue-next';

const mobileOpen = ref(false);
const showDemo = ref(false);
const toast = ref('');
const activeScene = ref('日常协同');

const scenes = {
  日常协同: {
    eyebrow: 'DAILY · 09:42',
    title: '让每位公牛员工\n都拥有一位懂业务的 AI 同行者',
    copy: '把会议、资料、跟进和复盘串成一条线，减少重复寻找和反复沟通。',
    steps: ['读取部门资料', '整理今日待办', '同步进度与风险', '沉淀可复用经验'],
    metric: '今日待处理',
    value: '24',
    delta: '↑ 18%'
  },
  新品推进: {
    eyebrow: 'PRODUCT · GT',
    title: '把跨部门的新品推进\n放在同一张工作盘上',
    copy: '让产品、市场、渠道和服务团队共享同一份上下文，按节点一起向前。',
    steps: ['汇总项目资料', '生成推进清单', '追踪关键里程碑', '同步渠道反馈'],
    metric: '已对齐节点',
    value: '18',
    delta: '↑ 6'
  },
  运营复盘: {
    eyebrow: 'OPS · REVIEW',
    title: '把经验沉淀下来\n让下一次执行更轻松',
    copy: '将高频流程沉淀成可复用的 Skill，在权限边界内稳定完成每一次复盘。',
    steps: ['汇总业务数据', '提炼行动建议', '形成可复用经验', '同步至团队工作盘'],
    metric: '本周已复用',
    value: '36h',
    delta: '↑ 22%'
  }
};

const scene = computed(() => scenes[activeScene.value]);

function scrollToContact() {
  mobileOpen.value = false;
  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
}

function showAccessMessage() {
  toast.value = '请使用公牛账号进入 公牛AI办公工作台';
  window.setTimeout(() => { toast.value = ''; }, 2800);
}

// 跑马灯数据（复制一份实现无缝循环）
const marqueeItems = [
  'AI 协作中心',
  '部门知识库',
  '任务工作盘',
  'Skill 复用',
  '连接器生态',
  '策略中心',
  '审计追溯',
  '额度管理'
];
const marqueeLoop = [...marqueeItems, ...marqueeItems];

// 数据统计
const stats = [
  { label: '部门已接入', value: '10', unit: '+', desc: '覆盖集团核心事业部' },
  { label: '工作盘任务', value: '4.6', unit: 'k/日', desc: '日均处理工作事项' },
  { label: 'Skill 复用率', value: '27', unit: '%', desc: '流程经验沉淀效率' },
  { label: '平均节省', value: '1.3', unit: 'h/人/天', desc: '重复工作耗时压缩' }
];
</script>

<template>
  <div class="site-shell">
    <header class="topbar">
      <a class="brand" href="#top" aria-label="公牛AI办公首页">
        <span class="brand-mark"><img src="/assets/bullwork-logo.png" alt="" /></span>
        <span class="brand-copy"><strong>公牛AI办公</strong><small>公牛集团 · AI Workspace</small></span>
      </a>
      <button class="menu-toggle icon-button" type="button" :aria-label="mobileOpen ? '关闭菜单' : '打开菜单'" @click="mobileOpen = !mobileOpen">
        <X v-if="mobileOpen" :size="18" /><Menu v-else :size="18" />
      </button>
      <nav class="main-nav" :class="{ open: mobileOpen }">
        <a href="#product" @click="mobileOpen = false">能力</a>
        <a href="#scenarios" @click="mobileOpen = false">场景</a>
        <a href="#trust" @click="mobileOpen = false">边界</a>
        <a href="#about" @click="mobileOpen = false">工作流</a>
        <div class="mobile-nav-actions">
          <button class="nav-link" type="button" @click="showDemo = true; mobileOpen = false"><Play :size="13" /> 使用方式</button>
          <button class="button button-primary" type="button" @click="showAccessMessage">进入工作台 <ArrowUpRight :size="15" /></button>
        </div>
      </nav>
      <div class="desktop-nav-actions">
        <button class="nav-link" type="button" @click="showDemo = true"><Play :size="13" /> 使用方式</button>
        <button class="button button-dark" type="button" @click="showAccessMessage">进入工作台 <ArrowUpRight :size="15" /></button>
      </div>
    </header>

    <main>
      <!-- ============ HERO ============ -->
      <section id="top" class="hero section-wrap">
        <div class="scanline" aria-hidden="true"></div>
        <div class="hero-copy">
          <div class="eyebrow"><span>公牛AI办公· 内部 AI 工作台</span></div>
          <h1>把日常交给 AI<br /><em>让公牛人专注判断</em></h1>
          <p class="hero-lead">公牛AI办公连接公牛的部门知识、流程与权限边界。它不只是回答问题，更帮你把任务推进到交付 — 从一句一句的指令，到一项一项完成的工作。</p>
          <div class="hero-actions">
            <button class="button button-primary" type="button" @click="showAccessMessage">进入 公牛AI办公<ArrowUpRight :size="16" /></button>
            <button class="button button-ghost" type="button" @click="showDemo = true"><span class="play-icon"><Play :size="12" fill="currentColor" /></span> 看看它如何工作</button>
          </div>
          <div class="hero-footnote"><ShieldCheck :size="14" /> 公牛账号 · 部门边界 · 审计追溯</div>
        </div>

        <div class="hero-visual" aria-label="公牛AI办公工作台界面预览">
          <!-- 浮动雷达 -->
          <div class="radar" aria-hidden="true">
            <div class="radar-ring"></div>
            <div class="radar-ring inner"></div>
            <div class="radar-ring core"></div>
            <div class="radar-cross"></div>
            <div class="radar-sweep"></div>
            <div class="radar-dot d1"></div>
            <div class="radar-dot d2"></div>
            <div class="radar-dot d3"></div>
          </div>

          <div class="dashboard-window">
            <div class="window-topbar">
              <span class="window-brand"><span class="mini-shield"><img src="/assets/bullwork-logo.png" alt="" /></span> 公牛AI办公</span>
              <span class="window-status">工作空间已连接</span>
              <span class="window-tabs"><span>实时</span><span class="active">工作盘</span><span>知识</span></span>
            </div>
            <div class="window-body">
              <aside class="window-sidebar">
                <span class="side-active"><Layers3 :size="14" /> 工作盘</span>
                <span><Workflow :size="14" /> 进行中</span>
                <span><Boxes :size="14" /> 知识库</span>
                <span><Network :size="14" /> 连接器</span>
                <span class="side-spacer"></span>
                <span><LockKeyhole :size="14" /> 策略中心</span>
              </aside>
              <div class="window-content">
                <div class="window-heading">
                  <div><small>MONDAY · 09:42</small><h3>早上好，今天一起把事情推进</h3></div>
                  <div class="avatar">林</div>
                </div>
                <div class="window-command">
                  <Sparkles :size="14" />
                  <span>告诉 公牛AI办公，你今天要完成什么...</span>
                  <ArrowUpRight :size="14" />
                </div>
                <div class="window-grid">
                  <div class="work-card work-card-large">
                    <div class="card-head"><span>我的工作盘</span><b>VIEW ALL <ArrowUpRight :size="11" /></b></div>
                    <div class="progress-row"><span class="progress-icon progress-green"><Check :size="12" /></span><span>渠道周报与销售复盘</span><strong>80%</strong></div>
                    <div class="progress-track"><i style="width:80%"></i></div>
                    <div class="progress-row"><span class="progress-icon progress-cyan"><CircleCheck :size="12" /></span><span>新品上市协同计划</span><strong>42%</strong></div>
                    <div class="progress-track"><i class="cyan" style="width:42%"></i></div>
                  </div>
                  <div class="work-card metric-card">
                      <small>本周已交付</small>
                      <strong>128</strong>
                      <span><span class="metric-up">↑ 18%</span> 较上周</span>
                      <div class="mini-bars">
                        <i></i><i></i><i></i><i></i><i></i><i></i><i class="bar-hot"></i><i class="bar-hot"></i>
                      </div>
                    </div>
                  <div class="work-card insight-card">
                    <span class="insight-label"><Sparkles :size="12" /> AI INSIGHT</span>
                    <p>发现 3 个待确认的上市风险，已为你整理成跟进清单。</p>
                    <button type="button">打开清单 <ArrowUpRight :size="11" /></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ MARQUEE ============ -->
      <section class="marquee" aria-hidden="true">
        <div class="marquee-track">
          <span v-for="(item, i) in marqueeLoop" :key="i">{{ item }}</span>
        </div>
      </section>

      <!-- ============ CAPABILITIES ============ -->
      <section id="product" class="capability-section section-wrap">
        <div class="section-intro">
          <div class="eyebrow dot-cyan"><span>Core capabilities</span></div>
          <h2>不是多一个工具，<br /><em>而是每天都能用上的帮手</em></h2>
          <p>公牛AI办公把公牛内部的模型、知识、Skill 和权限收拢到一个清晰的工作入口，帮你少一点重复操作，多一点有效推进。</p>
        </div>
        <div class="capability-grid">
          <article class="capability-item capability-red">
            <span class="capability-number">01 / THINK</span>
            <div class="capability-icon"><Sparkles :size="22" /></div>
            <h3>帮我想清楚</h3>
            <p>从一句自然语言开始，识别目标、上下文与交付标准，把模糊需求变成清晰行动。</p>
            <div class="capability-tags">
              <span>意图理解</span><span>任务拆解</span><span>上下文</span>
            </div>
            <a href="#scenarios">看看工作场景 <ArrowUpRight :size="14" /></a>
          </article>
          <article class="capability-item">
            <span class="capability-number">02 / FIND</span>
            <div class="capability-icon"><Network :size="22" /></div>
            <h3>帮我找得到</h3>
            <p>连接部门资料、项目文件与业务知识，让 AI 在正确的公牛工作语境里回答和协作。</p>
            <div class="capability-tags">
              <span>知识检索</span><span>权限边界</span><span>多源连接</span>
            </div>
            <a href="#trust">了解使用边界 <ArrowUpRight :size="14" /></a>
          </article>
          <article class="capability-item capability-cyan">
            <span class="capability-number">03 / SHIP</span>
            <div class="capability-icon"><CircleCheck :size="22" /></div>
            <h3>帮我做下去</h3>
            <p>把结果变成待办、清单和下一步动作，过程可回看，经验也能留给下一次。</p>
            <div class="capability-tags">
              <span>工作盘</span><span>Skill 沉淀</span><span>复盘</span>
            </div>
            <a href="#contact">开始使用 <ArrowUpRight :size="14" /></a>
          </article>
        </div>
      </section>

      <!-- ============ SCENARIOS ============ -->
      <section id="scenarios" class="scenarios-section">
        <div class="section-wrap scenarios-layout">
          <div class="scenarios-heading">
            <div class="eyebrow"><span>Work in motion</span></div>
            <h2>不同岗位，<br /><em>都有自己的工作节奏</em></h2>
            <p>同一个 公牛AI办公，适配公牛不同部门的任务、知识和责任边界。选择一个场景，看看工作如何被自然推进。</p>
            <div class="scene-tabs" role="tablist">
              <button v-for="key in Object.keys(scenes)" :key="key" type="button" :class="{ active: activeScene === key }" role="tab" :aria-selected="activeScene === key" @click="activeScene = key">
                <span>{{ key }}</span>
                <small>{{ scenes[key].eyebrow.split(' · ')[0] }} <ArrowUpRight :size="12" /></small>
              </button>
            </div>
          </div>
          <div class="scene-panel">
            <div class="scene-panel-top">
              <span>{{ scene.eyebrow }}</span>
              <span class="scene-live">LIVE WORKFLOW</span>
            </div>
            <h3>{{ scene.title }}</h3>
            <p>{{ scene.copy }}</p>
            <div class="scene-steps">
              <div v-for="(step, index) in scene.steps" :key="step" class="scene-step">
                <span>STEP·0{{ index + 1 }}</span>
                <b>{{ step }}</b>
                <Check :size="14" />
              </div>
            </div>
            <div class="scene-bottom">
              <div class="scene-metric">
                <small>{{ scene.metric }}</small>
                <strong>{{ scene.value }}</strong>
                <small>{{ scene.delta }} 较上周</small>
              </div>
              <div class="scene-thread">
                <span class="thread-avatar"><img src="/assets/bullwork-logo.png" alt="" /></span>
                <span>公牛AI办公正在协作</span>
                <i></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ STATS ============ -->
      <section class="stats-strip">
        <div class="section-wrap stats-grid">
          <div v-for="(s, i) in stats" :key="i" class="stat">
            <small>{{ s.label }}</small>
            <strong>{{ s.value }}<em>{{ s.unit }}</em></strong>
            <p>{{ s.desc }}</p>
          </div>
        </div>
      </section>

      <!-- ============ TRUST ============ -->
      <section id="trust" class="trust-section section-wrap">
        <div class="trust-visual">
          <div class="trust-shield-wrap">
            <span class="shield-ring c"></span>
            <span class="shield-ring b"></span>
            <span class="shield-ring a"></span>
            <div class="trust-shield"><img src="/assets/bullwork-logo.png" alt="公牛AI办公盾牌标志" /></div>
            <div class="trust-stamp"><ShieldCheck :size="14" /><span>BUILT ON<br /><b>TRUST</b></span></div>
          </div>
        </div>
        <div class="trust-copy">
          <div class="eyebrow dot-amber"><span>Made for 公牛AI办公inside</span></div>
          <h2>好用之外，<br /><em>更要用得安心</em></h2>
          <p>公牛AI办公是公牛内部的工作工具。它只在合适的知识边界内协作，把效率交给 AI，把判断和责任留给人。</p>
          <div class="trust-list">
            <div class="trust-item">
              <span class="trust-icon"><LockKeyhole :size="18" /></span>
              <div>
                <strong>只看该看的</strong>
                <p>按组织、部门和角色配置可见范围，工作资料不越界。</p>
                <div class="meta"><span>SCOPE</span><span>RBAC</span><span>部门隔离</span></div>
              </div>
            </div>
            <div class="trust-item cyan">
              <span class="trust-icon"><ShieldCheck :size="18" /></span>
              <div>
                <strong>只做该做的</strong>
                <p>模型、Skill 和连接器由管理员统一配置，能力有边界。</p>
                <div class="meta"><span>POLICY</span><span>审批</span><span>Skill 沙箱</span></div>
              </div>
            </div>
            <div class="trust-item amber">
              <span class="trust-icon"><Eye :size="18" /></span>
              <div>
                <strong>每一步可回看</strong>
                <p>关键操作自动留痕，调用、结果和风险都能追溯。</p>
                <div class="meta"><span>AUDIT</span><span>日志</span><span>导出</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ CASE / WORKFLOW ============ -->
      <section id="about" class="case-section section-wrap">
        <div class="case-copy">
          <div class="eyebrow dot-cyan"><span>From idea to impact</span></div>
          <h2>让业务经验，<br /><em>成为团队的共同能力</em></h2>
          <p>新品推进、渠道协作、客户服务、组织运营。公牛AI办公不替代公牛人的判断，而是把经验变成团队可以持续使用的工作方法。</p>
          <div class="case-meta">
            <span><Factory :size="14" /> 公牛内部实践</span>
            <span><Workflow :size="14" /> 从计划到执行</span>
            <span><Boxes :size="14" /> Skill 可复用</span>
          </div>
          <button class="text-button" type="button" @click="showDemo = true">查看使用方式 <ArrowUpRight :size="15" /></button>
        </div>

        <div class="case-image" aria-label="公牛AI办公工作流示意">
          <div class="case-image-header">
            <div class="dots"><i></i><i></i><i></i><span style="margin-left:10px">公牛AI办公· workflow.run</span></div>
            <span class="tag">RUN · OK</span>
          </div>
          <div class="case-image-body">
            <pre class="code-block"><span class="ln">1</span><span class="c">// 公牛AI办公· 一次新品上市协同</span>
<span class="ln">2</span><span class="k">const</span> task = <span class="k">await</span> bullwork.<span class="p">run</span>({
<span class="ln">3</span>  goal:  <span class="s">"Q3 新品上市协同"</span>,
<span class="ln">4</span>  scope: [<span class="s">"市场"</span>, <span class="s">"渠道"</span>, <span class="s">"客服"</span>],
<span class="ln">5</span>  skills: [<span class="s">"gtm-plan"</span>, <span class="s">"channel-sync"</span>]
<span class="ln">6</span>});
<span class="ln">7</span>
<span class="ln">8</span><span class="c">// 1. 汇总跨部门资料</span>
<span class="ln">9</span><span class="k">await</span> task.<span class="p">step</span>(<span class="s">"gather"</span>);
<span class="ln">10</span><span class="c">// 2. 拆解为节点并分配</span>
<span class="ln">11</span><span class="k">await</span> task.<span class="p">step</span>(<span class="s">"breakdown"</span>);
<span class="ln">12</span><span class="c">// 3. 进入工作盘持续推进</span>
<span class="ln">13</span><span class="k">await</span> task.<span class="p">step</span>(<span class="s">"execute"</span>);</pre>
            <div class="case-flow">
              <div class="flow-node">
                <small>NODE 01</small>
                <strong>资料汇总</strong>
                <span>跨部门知识自动归集</span>
              </div>
              <div class="flow-node live">
                <small>NODE 02 · LIVE</small>
                <strong>任务拆解</strong>
                <span>18 个节点自动分配</span>
              </div>
              <div class="flow-node">
                <small>NODE 03</small>
                <strong>交付追踪</strong>
                <span>关键风险实时告警</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ============ CTA ============ -->
      <section id="contact" class="contact-section section-wrap">
        <div class="contact-panel">
          <div class="contact-symbol"><img src="/assets/bullwork-logo.png" alt="" /></div>
          <div class="contact-copy">
            <div class="eyebrow"><span>Ready for daily work</span></div>
            <h2>下一项工作，<br /><em>现在就交给 公牛AI办公</em></h2>
            <p>使用公牛账号进入工作台，从资料查找、任务拆解或一份复盘开始。</p>
          </div>
          <div class="start-actions">
            <button class="button button-primary" type="button" @click="showAccessMessage">进入 公牛AI办公<ArrowUpRight :size="15" /></button>
            <button class="button button-ghost" type="button" @click="showDemo = true"><Play :size="13" /> 查看使用方式</button>
            <span class="form-note"><Check :size="13" /> 公牛内部工具 · 按组织权限使用</span>
          </div>
        </div>
      </section>
    </main>

    <footer class="footer section-wrap">
      <a class="brand footer-brand" href="#top">
        <span class="brand-mark"><img src="/assets/bullwork-logo.png" alt="" /></span>
        <span class="brand-copy"><strong>公牛AI办公</strong><small>公牛集团内部工具</small></span>
      </a>
      <div class="footer-links">
        <a href="#product">能力</a>
        <a href="#scenarios">场景</a>
        <a href="#trust">边界</a>
        <a href="#contact">开始</a>
      </div>
      <span class="footer-copy">© 2026 公牛AI办公· Bull Group MobotStone</span>
    </footer>

    <!-- ============ DEMO MODAL ============ -->
    <div v-if="showDemo" class="modal-backdrop" @click.self="showDemo = false">
      <div class="demo-modal" role="dialog" aria-modal="true" aria-label="公牛AI办公使用方式">
        <button class="modal-close icon-button" type="button" aria-label="关闭使用方式" @click="showDemo = false"><X :size="16" /></button>
        <div class="demo-screen">
          <div class="demo-screen-label">
            <span class="mini-shield"><img src="/assets/bullwork-logo.png" alt="" /></span> BullWork
            <span>HOW IT WORKS</span>
          </div>
          <div class="demo-play"><Play :size="26" fill="currentColor" /></div>
          <div class="demo-screen-caption">
            <strong>从一句话，到一项被完成的工作</strong>
            <span>理解任务 · 找到知识 · 推进结果</span>
          </div>
        </div>
        <div class="demo-modal-copy">
          <span class="eyebrow dot-red"><span>For daily work</span></span>
          <h3>AI 不只是回答，<br />它会陪你把事情做完</h3>
          <p>公牛AI办公将目标拆解、知识调用、协作推进和结果复盘连接起来，让每一次工作都有下一步。</p>
          <button class="button button-primary" type="button" @click="showDemo = false; showAccessMessage()">进入 公牛AI办公<ArrowUpRight :size="15" /></button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="toast" role="status">{{ toast }}</div>
  </div>
</template>